import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { LoadingOverlay, Center } from '@mantine/core';
import { AdminRoute } from '../components/routing/AdminRoute';
import { CustomerRoute } from '../components/routing/CustomerRoute';
import { NonProfitUserRoute } from '../components/routing/NonProfitUserRoute';
import { HomeRedirect } from '../components/routing/HomeRedirect';

// Layouts
import { AdminLayout } from '../layouts/AdminLayout';
import { CustomerLayout } from '../layouts/CustomerLayout';
import { NonProfitLayout } from '../layouts/NonProfitLayout';
import { PublicLayout } from '../layouts/PublicLayout';

// Page loading fallback component
const PageLoader: React.FC = () => (
  <Center h="100vh">
    <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />
  </Center>
);

// ============================================================
// LAZY-LOADED PAGES (heavy pages loaded on-demand)
// ============================================================

// RegisterPage - Heavy with Stripe integration (1,068 lines)
const RegisterPage = lazy(() =>
  import('../pages/Register/RegisterPage').then(m => ({ default: m.RegisterPage }))
);

// Admin Analytics - Uses Recharts for data visualization
const AdminAnalyticsPage = lazy(() =>
  import('../pages/Admin/AdminAnalyticsPage').then(m => ({ default: m.AdminAnalyticsPage }))
);

// Mention Review pages - Large admin features
const MentionReviewPage = lazy(() =>
  import('../pages/Admin/MentionReviewPage').then(m => ({ default: m.MentionReviewPage }))
);
const MentionReviewDetailPage = lazy(() =>
  import('../pages/Admin/MentionReviewDetailPage').then(m => ({ default: m.MentionReviewDetailPage }))
);

// Post Review pages - Large detail views (682 lines)
const PostReviewPage = lazy(() =>
  import('../pages/Admin/PostReviewPage').then(m => ({ default: m.PostReviewPage }))
);
const PostReviewDetailPage = lazy(() =>
  import('../pages/Admin/PostReviewDetailPage').then(m => ({ default: m.PostReviewDetailPage }))
);

// Company management pages - Company operations (686 lines)
const CompanyManagementPage = lazy(() =>
  import('../pages/Admin/CompanyManagementPage').then(m => ({ default: m.CompanyManagementPage }))
);
const CompanyDetailsPage = lazy(() =>
  import('../pages/Admin/CompanyDetailsPage').then(m => ({ default: m.CompanyDetailsPage }))
);
const CompanySuggestionsPage = lazy(() =>
  import('../pages/Admin/CompanySuggestionsPage').then(m => ({ default: m.CompanySuggestionsPage }))
);

// Alias Review - Admin-only (SESSION_218 - legacy, redirect to Admin Review)
const AliasReviewPage = lazy(() =>
  import('../pages/Admin/AliasReviewPage').then(m => ({ default: m.AliasReviewPage }))
);

// Admin Review Queue - Unified tabbed interface (SESSION_312)
const AdminReviewPage = lazy(() =>
  import('../pages/Admin/AdminReviewPage').then(m => ({ default: m.AdminReviewPage }))
);

// SESSION_221: Company Claiming pages
const CompanyClaimReviewPage = lazy(() =>
  import('../pages/Admin/CompanyClaimReviewPage').then(m => ({ default: m.CompanyClaimReviewPage }))
);
const LocationRequestReviewPage = lazy(() =>
  import('../pages/Admin/LocationRequestReviewPage').then(m => ({ default: m.LocationRequestReviewPage }))
);
const FranchiseManagementPage = lazy(() =>
  import('../pages/MyBusiness/FranchiseManagementPage').then(m => ({ default: m.FranchiseManagementPage }))
);

// SESSION_257: CRM pages
const CRMDashboardPage = lazy(() =>
  import('../pages/Admin/CRMDashboardPage').then(m => ({ default: m.default }))
);

// Sprint 062: Non-Profit Management pages
const NonProfitManagementPage = lazy(() =>
  import('../pages/Admin/NonProfitManagementPage').then(m => ({ default: m.NonProfitManagementPage }))
);
const NonProfitDetailPage = lazy(() =>
  import('../pages/Admin/NonProfitDetailPage').then(m => ({ default: m.NonProfitDetailPage }))
);
const NonProfitPhraseRequestsPage = lazy(() =>
  import('../pages/Admin/NonProfitPhraseRequestsPage').then(m => ({ default: m.NonProfitPhraseRequestsPage }))
);
const NonProfitMentionReviewPage = lazy(() =>
  import('../pages/Admin/NonProfitMentionReviewPage').then(m => ({ default: m.NonProfitMentionReviewPage }))
);

// Sprint 062 Session 7: Non-Profit User pages
const NonProfitInviteAcceptPage = lazy(() =>
  import('../pages/NonProfitInvite/NonProfitInviteAcceptPage').then(m => ({ default: m.NonProfitInviteAcceptPage }))
);
const NonProfitDashboardPage = lazy(() =>
  import('../pages/NonProfitDashboard/NonProfitDashboardPage').then(m => ({ default: m.NonProfitDashboardPage }))
);

// Sprint 065: Admin Funnel Viewer
const AdminFunnelPage = lazy(() =>
  import('../pages/Admin/AdminFunnelPage').then(m => ({ default: m.AdminFunnelPage }))
);

// Sprint 046: Email Analytics page
const EmailAnalyticsPage = lazy(() =>
  import('../pages/Admin/EmailAnalyticsPage').then(m => ({ default: m.default }))
);
const ContactsPage = lazy(() =>
  import('../pages/Admin/ContactsPage').then(m => ({ default: m.default }))
);
const ContactDetailPage = lazy(() =>
  import('../pages/Admin/ContactDetailPage').then(m => ({ default: m.default }))
);

// Business Detail - Location data, complex UI (672 lines)
const BusinessDetailPage = lazy(() =>
  import('../pages/Businesses').then(m => ({ default: m.BusinessDetailPage }))
);

// Sprint 046: Unsubscribe page (public, lazy loaded)
const UnsubscribePage = lazy(() =>
  import('../pages/Unsubscribe/UnsubscribePage').then(m => ({ default: m.UnsubscribePage }))
);

// Sprint 041: Marketing pages (lazy loaded for code splitting)
const MarketingHomePage = lazy(() =>
  import('../pages/Marketing').then(m => ({ default: m.MarketingHomePage }))
);
const HowItWorksPage = lazy(() =>
  import('../pages/Marketing').then(m => ({ default: m.HowItWorksPage }))
);
const MarketingBenefitsPage = lazy(() =>
  import('../pages/Marketing').then(m => ({ default: m.BenefitsPage }))
);
const MarketingFeaturesPage = lazy(() =>
  import('../pages/Marketing').then(m => ({ default: m.FeaturesPage }))
);
const MarketingExamplesPage = lazy(() =>
  import('../pages/Marketing').then(m => ({ default: m.ExamplesPage }))
);
const MarketingContactPage = lazy(() =>
  import('../pages/Marketing').then(m => ({ default: m.ContactPage }))
);
const MarketingSocialImpactPage = lazy(() =>
  import('../pages/Marketing').then(m => ({ default: m.SocialImpactPage }))
);

// ============================================================
// STATIC IMPORTS (smaller pages loaded immediately)
// ============================================================

// Pages
import { LoginPage } from '../pages/Login/LoginPage';
// SESSION_199: Onboarding page
import { OnboardingPage } from '../pages/Onboarding';
import { ForgotPasswordPage } from '../pages/ForgotPassword/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPassword/ResetPasswordPage';
// SESSION_189: Email confirmation pages
import { EmailConfirmationPendingPage, EmailConfirmedPage } from '../pages/EmailConfirmation';
// Sprint 037 Session 9: CustomerDashboard removed - MentionsPage is now the primary landing page
import { AdminDashboard } from '../pages/Dashboard/AdminDashboard';
import { MentionsPage } from '../pages/Mentions/MentionsPage';
import { RegionsPage, RegionDetailPage } from '../pages/Regions';
import { BusinessesPage } from '../pages/Businesses';
import { UsersPage } from '../pages/Users';
import { MyBusinessPage, AliasManagementPage } from '../pages/MyBusiness';
import { BillingPage } from '../pages/Billing';
import { SettingsPage } from '../pages/Settings';  // SESSION_201
import { ImportsPage } from '../pages/Admin/ImportsPage';
import { AddressCorrectionReviewPage } from '../pages/Admin/AddressCorrectionReviewPage';
import { AccessDenied } from '../pages/AccessDenied/AccessDenied';
import { NotFound } from '../pages/NotFound/NotFound';
import { ComponentTestPage } from '../pages/ComponentTestPage';
// SESSION_210: Legal pages
import { PrivacyPage } from '../pages/Privacy/PrivacyPage';
import { TermsPage } from '../pages/Terms/TermsPage';

// Route constants
import { PUBLIC_ROUTES, CUSTOMER_ROUTES, ADMIN_ROUTES, NONPROFIT_ROUTES, UTILITY_ROUTES, MARKETING_ROUTES } from './routes';

/**
 * AppRoutes - Main application routing configuration
 *
 * Route structure:
 * - Marketing routes: /, /benefits, /features, etc. (PublicLayout, HomeRedirect)
 * - Public routes: /register, /privacy, /terms (no layout or PublicLayout)
 * - Customer routes: /mentions, /my-business (CustomerLayout)
 * - Admin routes: /admin/* (AdminLayout)
 * - Utility routes: /access-denied, /404 (no layout)
 *
 * Sprint 041: Marketing pages consolidated from separate Marketing SWA.
 * HomeRedirect handles auth-aware routing at `/`.
 */
export const AppRoutes: React.FC = (): React.ReactElement => {
  return (
    <Routes>
      {/* Marketing/Public routes - no auth required */}
      <Route element={<HomeRedirect />}>
        <Route element={<PublicLayout />}>
          <Route path={MARKETING_ROUTES.HOME} element={<Suspense fallback={<PageLoader />}><MarketingHomePage /></Suspense>} />
          <Route path={MARKETING_ROUTES.HOW_IT_WORKS} element={<Suspense fallback={<PageLoader />}><HowItWorksPage /></Suspense>} />
          <Route path={MARKETING_ROUTES.BENEFITS} element={<Suspense fallback={<PageLoader />}><MarketingBenefitsPage /></Suspense>} />
          <Route path={MARKETING_ROUTES.FEATURES} element={<Suspense fallback={<PageLoader />}><MarketingFeaturesPage /></Suspense>} />
          <Route path={MARKETING_ROUTES.EXAMPLES} element={<Suspense fallback={<PageLoader />}><MarketingExamplesPage /></Suspense>} />
          <Route path={MARKETING_ROUTES.CONTACT} element={<Suspense fallback={<PageLoader />}><MarketingContactPage /></Suspense>} />
          <Route path={MARKETING_ROUTES.SOCIAL_IMPACT} element={<Suspense fallback={<PageLoader />}><MarketingSocialImpactPage /></Suspense>} />

          {/* Legal pages - also use PublicLayout */}
          <Route path={PUBLIC_ROUTES.PRIVACY} element={<PrivacyPage />} />
          <Route path={PUBLIC_ROUTES.TERMS} element={<TermsPage />} />
        </Route>
      </Route>

      {/* Register route - includes business claiming flow (lazy loaded - Stripe) */}
      <Route path={PUBLIC_ROUTES.REGISTER} element={<Suspense fallback={<PageLoader />}><RegisterPage /></Suspense>} />

      {/* Sprint 062 Session 7: Non-Profit invite acceptance - public, no auth required (Stripe) */}
      <Route path={PUBLIC_ROUTES.NONPROFIT_INVITE} element={<Suspense fallback={<PageLoader />}><NonProfitInviteAcceptPage /></Suspense>} />

      {/* Sprint 046: Unsubscribe page - public, no auth required */}
      <Route path={PUBLIC_ROUTES.UNSUBSCRIBE} element={<Suspense fallback={<PageLoader />}><UnsubscribePage /></Suspense>} />

      {/* SESSION_189: Email confirmation routes */}
      <Route path={PUBLIC_ROUTES.CONFIRM_EMAIL_PENDING} element={<EmailConfirmationPendingPage />} />
      <Route path={PUBLIC_ROUTES.EMAIL_CONFIRMED} element={<EmailConfirmedPage />} />

      {/* Auth routes */}
      <Route path={PUBLIC_ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={PUBLIC_ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={PUBLIC_ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />

      {/* Customer routes - protected with CustomerRoute guard */}
      <Route element={<CustomerRoute />}>
        {/* SESSION_199: Onboarding - full-page experience (no nav layout) */}
        <Route path={CUSTOMER_ROUTES.ONBOARDING} element={<OnboardingPage />} />

        {/* Routes with CustomerLayout (nav bar) */}
        <Route element={<CustomerLayout />}>
          <Route path={CUSTOMER_ROUTES.MENTIONS} element={<MentionsPage />} />
          <Route path={CUSTOMER_ROUTES.MY_BUSINESS_ALIASES} element={<AliasManagementPage />} />  {/* SESSION_220 */}
          <Route path={CUSTOMER_ROUTES.MY_FRANCHISE} element={<Suspense fallback={<PageLoader />}><FranchiseManagementPage /></Suspense>} />  {/* SESSION_221 */}
          <Route path={CUSTOMER_ROUTES.MY_BUSINESS} element={<MyBusinessPage />} />
          <Route path={CUSTOMER_ROUTES.BILLING} element={<BillingPage />} />
          <Route path={CUSTOMER_ROUTES.SETTINGS} element={<SettingsPage />} />  {/* SESSION_201 */}
          {/* TODO: Add more customer routes */}
          {/* <Route path={CUSTOMER_ROUTES.MENTION_DETAIL} element={<MentionDetailPage />} /> */}
        </Route>
      </Route>

      {/* Non-Profit User routes - protected with NonProfitUserRoute guard */}
      <Route element={<NonProfitUserRoute />}>
        <Route element={<NonProfitLayout />}>
          <Route path={NONPROFIT_ROUTES.DASHBOARD} element={<Suspense fallback={<PageLoader />}><NonProfitDashboardPage /></Suspense>} />
        </Route>
      </Route>

      {/* Admin routes - protected with AdminRoute guard and AdminLayout */}
      <Route element={<AdminRoute />}>
        <Route element={<AdminLayout />}>
          <Route path={ADMIN_ROUTES.DASHBOARD} element={<AdminDashboard />} />
          <Route path={ADMIN_ROUTES.ANALYTICS} element={<Suspense fallback={<PageLoader />}><AdminAnalyticsPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.MENTIONS} element={<MentionsPage />} />
          <Route path={ADMIN_ROUTES.MENTION_REVIEW} element={<Suspense fallback={<PageLoader />}><MentionReviewPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.MENTION_REVIEW_DETAIL} element={<Suspense fallback={<PageLoader />}><MentionReviewDetailPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.POST_REVIEW_DETAIL} element={<Suspense fallback={<PageLoader />}><PostReviewDetailPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.POST_REVIEW} element={<Suspense fallback={<PageLoader />}><PostReviewPage /></Suspense>} />

          {/* Regions */}
          <Route path={ADMIN_ROUTES.REGIONS} element={<RegionsPage />} />
          <Route path={ADMIN_ROUTES.REGION_DETAIL} element={<RegionDetailPage />} />

          {/* Businesses */}
          <Route path={ADMIN_ROUTES.BUSINESSES} element={<BusinessesPage />} />
          <Route path={ADMIN_ROUTES.BUSINESS_DETAIL} element={<Suspense fallback={<PageLoader />}><BusinessDetailPage /></Suspense>} />

          {/* Business Companies - Sprint 014 */}
          <Route path={ADMIN_ROUTES.COMPANY_SUGGESTIONS} element={<Suspense fallback={<PageLoader />}><CompanySuggestionsPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.COMPANIES} element={<Suspense fallback={<PageLoader />}><CompanyManagementPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.COMPANY_DETAIL} element={<Suspense fallback={<PageLoader />}><CompanyDetailsPage /></Suspense>} />

          {/* Users - Sprint 010 */}
          <Route path={ADMIN_ROUTES.USERS} element={<UsersPage />} />
          {/* <Route path={ADMIN_ROUTES.USER_DETAIL} element={<UserDetailPage />} /> */}

          {/* Imports - Sprint 011 */}
          <Route path={ADMIN_ROUTES.IMPORTS} element={<ImportsPage />} />

          {/* Address Corrections - SESSION 197 */}
          <Route path={ADMIN_ROUTES.ADDRESS_CORRECTIONS} element={<AddressCorrectionReviewPage />} />

          {/* Alias Review - SESSION_218 (legacy route) */}
          <Route path={ADMIN_ROUTES.ALIAS_REVIEW} element={<Suspense fallback={<PageLoader />}><AliasReviewPage /></Suspense>} />

          {/* Admin Review Queue - SESSION_312 (unified tabbed interface) */}
          <Route path={ADMIN_ROUTES.ADMIN_REVIEW} element={<Suspense fallback={<PageLoader />}><AdminReviewPage /></Suspense>} />

          {/* Company Claiming - SESSION_221 */}
          <Route path={ADMIN_ROUTES.COMPANY_CLAIM_REVIEW} element={<Suspense fallback={<PageLoader />}><CompanyClaimReviewPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.LOCATION_REQUEST_REVIEW} element={<Suspense fallback={<PageLoader />}><LocationRequestReviewPage /></Suspense>} />

          {/* Non-Profit Management - Sprint 062 */}
          <Route path={ADMIN_ROUTES.NONPROFIT_PHRASE_REQUESTS} element={<Suspense fallback={<PageLoader />}><NonProfitPhraseRequestsPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.NONPROFIT_MENTIONS} element={<Suspense fallback={<PageLoader />}><NonProfitMentionReviewPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.NONPROFITS} element={<Suspense fallback={<PageLoader />}><NonProfitManagementPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.NONPROFIT_DETAIL} element={<Suspense fallback={<PageLoader />}><NonProfitDetailPage /></Suspense>} />

          {/* Funnel Tracking - Sprint 065 */}
          <Route path={ADMIN_ROUTES.FUNNEL} element={<Suspense fallback={<PageLoader />}><AdminFunnelPage /></Suspense>} />

          {/* CRM - SESSION_257 */}
          <Route path={ADMIN_ROUTES.CRM_DASHBOARD} element={<Suspense fallback={<PageLoader />}><CRMDashboardPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.CRM_CONTACTS} element={<Suspense fallback={<PageLoader />}><ContactsPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.CRM_CONTACT_DETAIL} element={<Suspense fallback={<PageLoader />}><ContactDetailPage /></Suspense>} />
          <Route path={ADMIN_ROUTES.CRM_EMAIL_ANALYTICS} element={<Suspense fallback={<PageLoader />}><EmailAnalyticsPage /></Suspense>} />

          {/* Mention Detail */}
          {/* <Route path={ADMIN_ROUTES.MENTION_DETAIL} element={<MentionDetailPage />} /> */}
        </Route>
      </Route>

      {/* Utility routes - no layout */}
      <Route path={UTILITY_ROUTES.ACCESS_DENIED} element={<AccessDenied />} />
      <Route path={UTILITY_ROUTES.NOT_FOUND} element={<NotFound />} />
      <Route path="/component-test" element={<ComponentTestPage />} />

      {/* Catch-all route - 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
